'use client'

import type { ElementType, ReactNode } from 'react'

import cn from 'clsx'
import { Box } from '@/components/Box/Box'
import { Button } from '@/components/Button/Button'
import { Card } from '@/components/Card/Card'
import { Heading } from '@/components/Text/Heading'
import { Text } from '@/components/Text/Text'
import { ExternalLink, ChevronRight, Article } from '@doist/icons/brand'
import css from './article-card.module.css'

type CommonProps = {
    title: string
    label: string
    href: string
    tag?: ElementType
    disabled?: boolean
    buttonIcons?: boolean
    buttonTarget?: '_blank' | '_self'
}

type BaseProps = {
    variant?: 'base'
    children: ReactNode
}

type AdjacentProps = {
    variant?: 'adjacent'
    children?: never
}

export type ArticleCardProps = CommonProps & (BaseProps | AdjacentProps)

export function ArticleCard({
    title,
    label,
    variant = 'base',
    children,
    href,
    disabled,
    buttonIcons = true,
    buttonTarget = '_blank',
    ...rest
}: ArticleCardProps) {
    const isBaseVariant = variant === 'base'
    const StartIcon = isBaseVariant && buttonIcons ? ExternalLink : undefined
    const EndIcon = !isBaseVariant && buttonIcons ? ChevronRight : undefined

    return (
        <Card className={cn(css.articleCard, css[variant], { [css.disabled]: disabled })} {...rest}>
            <Box className={css.copyContainer}>
                <Box className={css.titleContainer}>
                    <Article width={18} height={18} className={css.articleIcon} aria-hidden />
                    <Heading className={css.title} level="3" size="minor-base" weight="medium">
                        {title}
                    </Heading>
                </Box>
                {children && isBaseVariant ? (
                    <Text tag="span" size="body-xs" className={css.description}>
                        {children}
                    </Text>
                ) : null}
            </Box>
            <Button
                variant="tertiary"
                href={href}
                bleed={isBaseVariant ? 'inline' : undefined}
                startIcon={StartIcon}
                endIcon={EndIcon}
                className={css.button}
                size="small"
                aria-label={`${label} ${title}`}
                disabled={disabled}
                target={buttonTarget}
            >
                {label}
            </Button>
        </Card>
    )
}
